import { cn } from '@/shared/utils';
import { isEmptyString } from '@/shared/utils/string';
import { ArrowDownIcon, ArrowDownUp, ArrowUpIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Checkbox } from '../check-box/check-box';
import { List } from '../list/list';
import { Button } from '../shadcn/button';
import { TableStyle } from './table-style';
import type { BaseTableProps, RowData, SortStatus, TableColumnT } from './base-table-types';

export const BaseTable = <T,>(props: BaseTableProps<T>) => {
  const {
    columns,
    data,
    searchValue,
    onRowClick,
    defaultSortKey,
    placeholder,
    tableClassName,
    headerClassName,
    rowClassName,
    size,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = props;

  const sortMode = props.sortStatus ? 'controlled' : 'uncontrolled';

  const [internalSortStatus, setInternalSortStatus] = useState<SortStatus>({
    sortKey: defaultSortKey || '',
    orderStatus: 'desc',
  });

  const currSortStatus = useMemo(() => {
    if (sortMode === 'controlled') {
      return props.sortStatus;
    }
    return internalSortStatus;
  }, [internalSortStatus, props.sortStatus, sortMode]);

  const onSortChangeByHeader = (sortStatus: SortStatus) => {
    if (sortMode === 'controlled') {
      props.onSortChange?.(sortStatus);
    } else {
      setInternalSortStatus(sortStatus);
    }
  };

  const sortedData = useMemo(() => {
    return data.sort((a, b) => {
      const aColumn = columns.find((column) => column.columnKey === currSortStatus?.sortKey);
      if (!aColumn?.sortable) return 0;
      if (currSortStatus?.orderStatus === 'desc') {
        return aColumn.sort(a.rowData, b.rowData) || 0;
      }
      return aColumn.sort(b.rowData, a.rowData) || 0;
    });
  }, [data, columns, currSortStatus]);

  const computedColumns: TableColumnT<T>[] = useMemo(() => {
    if (props.useCheckbox) {
      const currentAllChecked = sortedData.every((item) =>
        props.checkedList.some((id) => id === item.rowKey),
      );

      return [
        {
          columnKey: 'checkbox',
          Header: (
            <Checkbox
              className="h-full w-full"
              checked={props.checkedList.length > 0 && currentAllChecked}
              halfCheck={props.checkedList.length > 0 && !currentAllChecked}
              onChange={(checked) => {
                props.setCheckedList((prev) => {
                  if (checked) {
                    const newCheckedList = new Set([
                      ...prev,
                      ...sortedData.map((item) => item.rowKey),
                    ]);
                    return [...newCheckedList];
                  }

                  return prev.filter((id) => !sortedData.some((item) => item.rowKey === id));
                });
              }}
            />
          ),
          Cell: (rowData: RowData<T>) => (
            <Checkbox
              className="h-full w-full"
              checked={props.checkedList.includes(rowData.rowKey)}
              onChange={(checked) =>
                props.setCheckedList((prev) => {
                  if (checked) {
                    const newCheckedList = new Set([...prev, rowData.rowKey]);
                    return [...newCheckedList];
                  }
                  return prev.filter((id) => id !== rowData.rowKey);
                })
              }
            />
          ),
          className: 'w-14 px-2',
        },
        ...columns,
      ];
    }
    return columns;
  }, [columns, props, sortedData]);

  return (
    <TableStyle.Table className={cn('relative flex-1 overflow-auto', tableClassName)}>
      <TableStyle.Header className={cn('sticky top-0 z-10', headerClassName)} size={size}>
        {computedColumns.map((column) => (
          <TableStyle.Col
            key={column.columnKey}
            className={cn('flex w-10 items-center gap-1 px-2', column.className)}
          >
            {column.Header}
            {column.sortable && (
              <Button
                onClick={() => {
                  onSortChangeByHeader({
                    sortKey: column.columnKey,
                    orderStatus:
                      currSortStatus?.sortKey === column.columnKey
                        ? currSortStatus?.orderStatus === 'asc'
                          ? 'desc'
                          : 'asc'
                        : 'desc',
                  });
                }}
              >
                {currSortStatus?.sortKey !== column.columnKey ? (
                  <ArrowDownUp />
                ) : currSortStatus?.orderStatus === 'asc' ? (
                  <ArrowUpIcon className="size-3" />
                ) : (
                  <ArrowDownIcon className="size-3" />
                )}
              </Button>
            )}
          </TableStyle.Col>
        ))}
      </TableStyle.Header>
      <List
        className="overflow-auto"
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        data={sortedData}
        placeholder={
          !isEmptyString(searchValue) && sortedData.length === 0
            ? {
                message: 'No results found',
              }
            : placeholder || {
                message: 'No data',
              }
        }
        render={(item, rowIndex) => {
          return (
            <TableStyle.Row
              key={item.rowKey}
              onClick={() => onRowClick?.(item.rowKey)}
              className={cn(rowClassName)}
            >
              {computedColumns.map((column) => (
                <TableStyle.Cell
                  key={column.columnKey}
                  className={cn('px-2', column.className)}
                  size={size}
                >
                  {column.Cell(
                    {
                      rowKey: item.rowKey,
                      rowData: item.rowData,
                    },
                    { rowIndex },
                  )}
                </TableStyle.Cell>
              ))}
            </TableStyle.Row>
          );
        }}
      />
    </TableStyle.Table>
  );
};
